import axios from "axios";
import { apiEndpoint, apiEndpointFunction } from "@iso/lib/helpers/endpoint";
import { authHeader } from "@iso/lib/helpers/authHeader";

export const sensorViewService = {
  getList,
  getDetail,
  create,
  update,
  destroy,
  getSensors,
  addSensors,
  removeSensors,
};

/**
 * Get list of sensor views
 * @param {Object} params - Query parameters (property_id, is_default, search, page, pageSize, sort)
 * @returns {Promise}
 */
function getList(params = {}) {
  return axios
    .get(apiEndpoint.sensorViews, {
      params: params,
      headers: authHeader(),
    })
    .then((res) => {
      if (res && res.status === 200 && res.data.code === "0000") {
        return res.data;
      }
    });
}

/**
 * Get single sensor view with full sensor timeline data
 * @param {Number} id - View ID
 * @param {Object} params - Query parameters (start_date, end_date, timezone, is_show_sensors)
 * @returns {Promise}
 */
function getDetail(id, params = {}) {
  return axios
    .get(apiEndpointFunction.sensorViews(id), {
      params: params,
      headers: authHeader(),
    })
    .then((res) => {
      if (res && res.status === 200 && res.data.code === "0000") {
        return res.data;
      }
    });
}

/**
 * Create a new sensor view
 * @param {Object} data - View data { property_id, name, description, is_default, sensor_ids }
 * @returns {Promise}
 */
function create(data) {
  return axios
    .post(apiEndpoint.sensorViewsCreate, data, {
      headers: authHeader(),
    })
    .then((res) => {
      if (res && res.status === 200 && res.data.code === "0000") {
        return res.data;
      }
    });
}

/**
 * Update an existing sensor view
 * @param {Number} id - View ID
 * @param {Object} data - Updated view data { name, description, is_default, sensor_ids }
 * @returns {Promise}
 */
function update(id, data) {
  return axios
    .post(apiEndpointFunction.sensorViewsUpdate(id), data, {
      headers: authHeader(),
    })
    .then((res) => {
      if (res && res.status === 200 && res.data.code === "0000") {
        return res.data;
      }
    });
}

/**
 * Delete a sensor view
 * @param {Number} id - View ID
 * @returns {Promise}
 */
function destroy(id) {
  return axios
    .post(
      apiEndpointFunction.sensorViewsDelete(id),
      {},
      {
        headers: authHeader(),
      }
    )
    .then((res) => {
      if (res && res.status === 200 && res.data.code === "0000") {
        return res.data;
      }
    });
}

/**
 * Get basic sensor list for a view (without timeline data)
 * @param {Number} id - View ID
 * @returns {Promise}
 */
function getSensors(id) {
  return axios
    .get(apiEndpointFunction.sensorViewsGetSensors(id), {
      headers: authHeader(),
    })
    .then((res) => {
      if (res && res.status === 200 && res.data.code === "0000") {
        return res.data;
      }
    });
}

/**
 * Add sensors to a view
 * @param {Number} id - View ID
 * @param {Array} sensorIds - Array of sensor IDs to add
 * @returns {Promise}
 */
function addSensors(id, sensorIds) {
  return axios
    .post(
      apiEndpointFunction.sensorViewsAddSensors(id),
      { sensor_ids: sensorIds },
      {
        headers: authHeader(),
      }
    )
    .then((res) => {
      if (res && res.status === 200 && res.data.code === "0000") {
        return res.data;
      }
    });
}

/**
 * Remove sensors from a view
 * @param {Number} id - View ID
 * @param {Array} sensorIds - Array of sensor IDs to remove (optional, removes all if not provided)
 * @returns {Promise}
 */
function removeSensors(id, sensorIds = null) {
  const data = sensorIds ? { sensor_ids: sensorIds } : {};
  
  return axios
    .post(apiEndpointFunction.sensorViewsRemoveSensors(id), data, {
      headers: authHeader(),
    })
    .then((res) => {
      if (res && res.status === 200 && res.data.code === "0000") {
        return res.data;
      }
    });
}